import React, { useState, useEffect, useCallback } from "react";
import { Card, CardContent } from "../../Components/ui/card";
import { Dollar, info, refresh } from "../../Public/assets/index";
import { Input } from "../../Components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../../Components/ui/select";
import { useOverview } from "./../../hooks";
import { TransferApi } from "./../../api";
import Loader from "./../../Components/Loader/PageLoader";
import { useDebounce } from "./../../Utils/helperFunction";
import {
  CONVERSION_INPUT_TYPES,
  TRANSACTION_TYPE,
  TRANSFER_METHOD,
} from "./../../Utils/Contant";

// Component for currency selector
const CurrencySelector = ({
  value,
  label,
  onChange,
  currencies,
  className,
  error,
}) => (
  <Select value={value || ""} onValueChange={onChange}>
    <SelectTrigger
      className={`w-[110px] font-geomanistBold border-l-0 rounded-l-none bg-greyShade ${
        error ? "border-red-500 border-l-0" : ""
      } ${className}`}
    >
      <SelectValue />
    </SelectTrigger>
    <SelectContent className="z-[9999]">
      {currencies?.map((currency, index) => (
        <SelectItem
          isLastItem={index === currencies.length - 1 ? "true" : undefined}
          key={currency.vendorCurrency}
          value={currency.vendorCurrency}
          className={
            currency.isFaded && label !== "To"
              ? "opacity-50 transition-opacity duration-500 pointer-events-none cursor-not-allowed"
              : ""
          }
          disabled={currency.isFaded && label !== "To"}
        >
          <div className="flex items-center">
            <span className="mr-2">
              <img src={currency?.flag} alt="" />
            </span>
            <span className="font-geomanistMedium">
              {currency?.vendorCurrency}
            </span>
          </div>
        </SelectItem>
      ))}
    </SelectContent>
  </Select>
);

// Component for amount input with currency selector
const AmountInput = ({
  label,
  amount,
  onAmountChange,
  currency,
  onCurrencyChange,
  currencies,
  error,
  availableAmount,
  loading,
}) => (
  <div className="space-y-2">
    <p className="text-xs font-normal text-blackShade">{label}</p>
    <div className="space-y-1">
      <div className="flex items-center">
        <Input
          type="number"
          value={amount}
          onChange={onAmountChange}
          className={`text-sm border-r-0 font-geomanistBold rounded-r-none ${
            error ? "border-red-500" : ""
          }`}
          disabled={loading}
        />
        <CurrencySelector
          label={label}
          value={currency?.vendorCurrency}
          onChange={onCurrencyChange}
          currencies={currencies}
          error={error}
        />
      </div>
      {error && <p className="text-red-500 text-xs mt-1">{error}</p>}

      {availableAmount && (
        <p className="text-[12px] space-x-2 font-normal text-greyish flex justify-end">
          Available Amount: {availableAmount}
        </p>
      )}
    </div>
  </div>
);

// Component for conversion details
const ConversionDetails = ({
  selectedCurrency,
  selectedToCurrency,
  charges,
  isLoading,
}) => {
  // Helper function to display fee
  const getFeeDisplay = () => {
    if (isLoading) {
      return <Loader className="w-6 h-6" />;
    }

    if (!charges?.totalFees || charges.totalFees === "-") {
      return "-";
    }

    const isZero = parseFloat(charges.totalFees) === 0;
    return isZero
      ? "Free"
      : `${charges.totalFees} ${selectedCurrency?.vendorCurrency}`;
  };

  const feeDisplay = getFeeDisplay();

  return (
    <div className="flex flex-col gap-5 relative">
      <div className="absolute left-4 top-0 bottom-0 border-l-2 border-dotted border-greyish"></div>

      {/* Conversion Rate */}
      <div className="">
        <div className="absolute left-[9px] bg-white z-50">
          <div className="text-blue-500 flex pt-1 items-center transform">
            <img src={refresh} alt="" />
          </div>
        </div>

        <div className="space-y-2 pl-12">
          <div className="flex items-center">
            <p className="text-xs flex items-center gap-2 font-normal text-[#797979]">
              Conversion Rate
              <div className="flex gap-1 h-6 items-center text-base font-geomanistBold text-[#22212E]">
                <span className="flex items-center text-base font-geomanistBold text-[#22212E]">
                  1 {selectedCurrency?.vendorCurrency} =
                </span>
                {isLoading ? <Loader className="w-6 h-6" /> : charges?.rate}{" "}
                {selectedToCurrency?.vendorCurrency}
              </div>
              <div className="relative group">
                <img src={info} alt="" />
                <div className="absolute left-1/2 -translate-x-1/2 hidden group-hover:block bottom-full w-max mb-2 bg-blueish font-geomanistBold text-white text-xs leading-[24px] rounded px-2 py-1">
                  Actual rate may vary
                  <div className="absolute bottom--2 left-1/2 -translate-x-1/2 w-2 h-2 bg-blueish rotate-45 transform"></div>
                </div>
              </div>
            </p>
          </div>
        </div>
      </div>

      {/* Conversion Fee */}
      <div>
        <div className="absolute left-[6px] bottom-0 bg-white z-50">
          <img src={Dollar} alt="" />
        </div>
        <div className="space-y-2 pl-12">
          <div className="flex items-center gap-2">
            <p className="text-xs flex items-center gap-2 font-normal text-[#797979]">
              Conversion Fee:
              <span className="flex gap-1 text-base font-geomanistBold text-[#22212E]">
                {feeDisplay}
              </span>
            </p>

            {feeDisplay === "-" || feeDisplay === "Free" ? (
              ""
            ) : (
              <div className="relative group">
                <img src={info} alt="" />
                <div className="absolute left-1/2 -translate-x-1/2 hidden group-hover:block bottom-full w-max mb-2 bg-blueish font-geomanistBold text-white text-xs leading-[24px] rounded px-2 py-1">
                  Actual fee may vary depending on the rate
                  <div className="absolute bottom--2 left-1/2 -translate-x-1/2 w-2 h-2 bg-blueish rotate-45 transform"></div>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

// Main component
const WalletTransfer = () => {
  const {
    getFlagByCurrencyCode,
    amount,
    setAmount,
    toAmount,
    setToAmount,
    charges,
    setCharges,
    selectedCurrency,
    setSelectedCurrency,
    selectedToCurrency,
    setSelectedToCurrency,
    transferCurrency,
    setTransferCurrency,
    fromAmountConversionError,
    setFromAmountConversionError,
    isLoading,
    setIsLoading,
    transferToCurrency,
    setTransferToCurrency,
    // react query
    useGlobalAccount,
    useWalletDetails,
    validateAmount,
    toAmountError,
    setToAmountError,
  } = useOverview();

  const { data: walletData } = useWalletDetails();
  const { data: globalAccountData } = useGlobalAccount();

  // const [toAmountError, setToAmountError] = useState("");
  const [conversionInputActivity, setConversionInputActivity] = useState(
    CONVERSION_INPUT_TYPES.NONE
  );

  // Process wallet data into usable currency options
  const currencies = walletData?.data?.accounts?.map((account) => ({
    flag: getFlagByCurrencyCode(account.vendorCurrency),
    balance: account.balance,
    vendorCurrency: account.vendorCurrency,
    isFaded: !(parseFloat(account.balance) - parseFloat(account.holdBalance) > 0),
      ...account,
    holdBalance: account.holdBalance || 0,
  }));

  // Initial setup when transferCurrency changes
  useEffect(() => {
    const fetchConversionData = async () => {
      if (!walletData?.data?.accounts || !transferCurrency) return;

      const foundCurrency = walletData.data.accounts.find(
        (e) => e.vendorCurrency === transferCurrency
      );

      if (!foundCurrency) return;

      setSelectedCurrency(foundCurrency);
      if (foundCurrency.vendorCurrency === transferToCurrency) {
        setTransferToCurrency(null);
      }

      const toCurrency = walletData.data.accounts.find(
        (e) =>
          e.vendorCurrency !== foundCurrency.vendorCurrency &&
          (e.vendorCurrency === transferToCurrency || !transferToCurrency)
      );

      if (!toCurrency) return;
      setSelectedToCurrency(toCurrency);

      if (amount && toAmount) return;

      try {
        const conversionCheck = await TransferApi({
          buyAmount: 1,
          toCurrency: transferToCurrency || toCurrency.vendorCurrency,
          fromCurrency: foundCurrency.vendorCurrency,
          transactionType: TRANSACTION_TYPE,
          selectedMethodToTransfer: TRANSFER_METHOD,
        });

        setCharges({
          totalFees: amount ? conversionCheck?.data?.totalFees : "-",
          rate: conversionCheck?.data?.rate,
        });
      } catch (err) {
        console.error("Error fetching conversion data:", err);
      }
    };

    fetchConversionData();
  }, [transferCurrency, walletData, transferToCurrency]);

  // Handle currency change for "From" field
  const handleCurrencyChange = (value) => {
    if (value === selectedToCurrency?.vendorCurrency) {
      const alternateCurrency = currencies.find(
        (e) => e?.vendorCurrency !== value
      );
      setTransferToCurrency(alternateCurrency?.vendorCurrency);
      setSelectedToCurrency(alternateCurrency?.vendorCurrency);
      setTransferCurrency(value);
    } else {
      setTransferCurrency(value);
    }

    setFromAmountConversionError("");
    setToAmountError("");
    setConversionInputActivity(CONVERSION_INPUT_TYPES.FROM);
  };

  // Handle currency change for "To" field
  const handleToCurrencyChange = (value) => {
    const selected = currencies.find(
      (currency) => currency.vendorCurrency === value
    );
    // setTransferCurrency(selected);
    setSelectedToCurrency(selected);
    setFromAmountConversionError("");
    setToAmountError("");
    setTransferToCurrency(value);
    setConversionInputActivity(CONVERSION_INPUT_TYPES.FROM);
  };

  // Handle amount change for both fields
  const handleAmountChange = (e, isFromAmount = true) => {
    const inputValue = e.target.value;
    setFromAmountConversionError("");

    if (isFromAmount) {
      setConversionInputActivity(CONVERSION_INPUT_TYPES.FROM);
      // Validate from amount
      if (selectedCurrency) {
        const availableBalance = (
          selectedCurrency.balance - selectedCurrency.holdBalance
        )?.toFixed(2);

        const amountError = validateAmount(
          inputValue,
          availableBalance,
          charges?.totalFees,
          2
        );
        setFromAmountConversionError(amountError);
      }
      if (inputValue === "") {
        setAmount("");
        setToAmount("");
        // setFromAmountConversionError("Please enter a valid amount");
      } else {
        setAmount(inputValue);
      }
    } else {
      setToAmountError("");

      // Validate to amount (minimum amount check only; balance check
      // doesn't apply here since this is the destination currency)
      const amountError = validateAmount(inputValue, Infinity, 0, 2);
      setToAmountError(amountError);

      setConversionInputActivity(CONVERSION_INPUT_TYPES.TO);
      if (inputValue === "") {
        setAmount("");
        setToAmount("");
      } else {
        setToAmount(inputValue);
      }
    }
  };

  const getAvailableBalance = () => {
    if (!selectedCurrency) return "";
    return parseFloat(
      selectedCurrency?.balance - selectedCurrency?.holdBalance
    ).toFixed(2);
  };
  const availableAmountDisplay2 = getAvailableBalance();

  // Debounced API call for conversion
  const debouncedFetchWalletTransfer = useCallback(
    useDebounce(async (payload) => {
      try {
        if ((amount || toAmount) && conversionInputActivity) {
          setFromAmountConversionError("");
          setToAmountError("");
          setIsLoading(true);

          const transfer = await TransferApi(payload);

          setCharges({
            totalFees: transfer?.data?.totalFees,
            rate: transfer?.data?.rate,
          });

          setToAmount(transfer?.data?.toAmount);
          setAmount(transfer?.data?.fromAmount);

          const amountError = validateAmount(
            transfer?.data?.fromAmount,
            availableAmountDisplay2,
            transfer?.data?.totalFees,
            2
          );
          setFromAmountConversionError(amountError);

          const toAmountValidationError = validateAmount(
            transfer?.data?.toAmount,
            Infinity,
            0,
            2
          );
          setToAmountError(toAmountValidationError);

          setConversionInputActivity(CONVERSION_INPUT_TYPES.NONE);
        }
      } catch (err) {
        console.error("Error during conversion:", err);
      } finally {
        setIsLoading(false);
      }
    }, 1500),
    [amount, toAmount, conversionInputActivity, selectedCurrency]
  );

  useEffect(() => {
    if (!selectedCurrency?.vendorCurrency) return;
    if (!selectedToCurrency?.vendorCurrency) return;
    if (!amount && !toAmount && !conversionInputActivity) return;

    // if (!selectedCurrency || !selectedToCurrency || (!amount && !toAmount))
    //   return;

    // if (fromAmountConversionError) return;

    const basePayload = {
      fromCurrency: selectedCurrency.vendorCurrency,
      toCurrency: selectedToCurrency.vendorCurrency,
      transactionType: TRANSACTION_TYPE,
      selectedMethodToTransfer: TRANSFER_METHOD,
    };

    if (conversionInputActivity === CONVERSION_INPUT_TYPES.FROM && amount) {
      debouncedFetchWalletTransfer({
        ...basePayload,
        sellAmount: amount,
      });
    } else if (
      conversionInputActivity === CONVERSION_INPUT_TYPES.TO &&
      toAmount
    ) {
      debouncedFetchWalletTransfer({
        ...basePayload,
        buyAmount: toAmount,
      });
    } else if (conversionInputActivity && !amount && !toAmount) {
      // Force a conversion with default value of 1 if currency changed but no amount entered
      debouncedFetchWalletTransfer({
        ...basePayload,
        sellAmount: 1,
      });
    }
  }, [
    selectedCurrency,
    selectedToCurrency,
    amount,
    toAmount,
    conversionInputActivity,
    fromAmountConversionError,
  ]);

  // Calculate available amount display
  const getAvailableAmountDisplay = () => {
    if (!selectedCurrency) return "";

    const availableAmount = parseFloat(
      selectedCurrency.balance - selectedCurrency.holdBalance
    )?.toFixed(2);

    return `${availableAmount} ${selectedCurrency.vendorCurrency}`;
  };

  const availableAmountDisplay = getAvailableAmountDisplay();

  return (
    <div className="py-[19px] space-y-[19px]">
      {/* User Account Card */}
      <Card className="shadow-send-money-gradient bg-[#F7F7F8] border-[1px] border-[#D3D4DA]">
        <CardContent className="p-4 gap-4 flex items-center">
          <div className="space-y-1">
            <p className="font-geomanistBold text-sm text-blackShade">
              {globalAccountData?.data?.user?.name}
            </p>
            <p className="text-xs font-geomanistBold text-blackShade">
              *******{globalAccountData?.data?.vendorAccountNo?.slice(-4)}
              <span className="pl-[6px] text-greyish">
                {globalAccountData?.data?.bankName}
              </span>
            </p>
          </div>
        </CardContent>
      </Card>

      {/* From Currency Input */}
      <AmountInput
        label="From"
        amount={amount}
        onAmountChange={(e) => handleAmountChange(e, true)}
        currency={selectedCurrency}
        onCurrencyChange={handleCurrencyChange}
        currencies={currencies}
        error={fromAmountConversionError}
        availableAmount={availableAmountDisplay}
        loading={isLoading}
      />

      {/* Conversion Details */}
      <ConversionDetails
        selectedCurrency={selectedCurrency}
        selectedToCurrency={selectedToCurrency}
        charges={charges}
        isLoading={isLoading}
      />

      {/* To Currency Input */}
      <AmountInput
        label="To"
        amount={toAmount}
        onAmountChange={(e) => handleAmountChange(e, false)}
        currency={selectedToCurrency}
        onCurrencyChange={handleToCurrencyChange}
        loading={isLoading}
        currencies={currencies?.filter(
          (currency) =>
            currency?.vendorCurrency !== selectedCurrency?.vendorCurrency
        )}
        error={toAmountError}
      />
    </div>
  );
};

export default WalletTransfer;
